import { useState, useEffect, useCallback } from 'react'
import { Zap, Plus, Save, Trash2, X, ChevronRight, Link2, AlertTriangle, Eye } from 'lucide-react'
import api from '../api/client'
import { UFS } from '../constants/uf'

const inp = 'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-accent-500'
const lbl = 'block text-[11px] font-semibold text-neutral-500 uppercase tracking-wide mb-1'
const card = 'bg-white rounded-2xl border border-neutral-200 shadow-sm'

export default function Distribuidoras() {
  const [lista, setLista] = useState([])
  const [geradores, setGeradores] = useState([])
  const [sel, setSel] = useState(null)
  const [preview, setPreview] = useState(null)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  const carregar = useCallback(async () => {
    const [d, g] = await Promise.all([api.get('/distribuidoras?limit=500'), api.get('/geradoras?limit=200')])
    setLista(d.data.data || [])
    setGeradores(g.data.data || [])
  }, [])
  useEffect(() => { carregar() }, [carregar])

  function abrir(d) {
    setMsg(''); setPreview(null)
    setSel(d || { nome: '', sigla: '', estado: '', status_operacional: 'ativa', gerador_principal_id: null, gerador_secundario_id: null, avisos_operacionais: '' })
  }
  const set = (k, v) => setSel((s) => ({ ...s, [k]: v }))

  async function salvar() {
    if (!sel.nome?.trim() || !sel.estado) { setMsg('Informe nome e estado.'); return }
    if (sel.gerador_principal_id && sel.gerador_principal_id === sel.gerador_secundario_id) { setMsg('Gerador principal e secundário devem ser diferentes.'); return }
    setBusy(true); setMsg('')
    const payload = {
      nome: sel.nome, sigla: sel.sigla, estado: sel.estado,
      status_operacional: sel.status_operacional,
      ativo: sel.status_operacional !== 'inativa',
      gerador_principal_id: sel.gerador_principal_id || null,
      gerador_secundario_id: sel.gerador_secundario_id || null,
      avisos_operacionais: sel.avisos_operacionais || null,
    }
    try {
      if (sel.id) await api.put(`/distribuidoras/${sel.id}`, payload)
      else await api.post('/distribuidoras', payload)
      await carregar(); setSel(null)
    } catch (e) { setMsg(e?.response?.data?.detail || 'Erro ao salvar.') } finally { setBusy(false) }
  }
  async function excluir() {
    if (!sel.id || !confirm('Excluir distribuidora?')) return
    await api.delete(`/distribuidoras/${sel.id}`); setSel(null); carregar()
  }
  async function verHeranca() {
    if (!sel.id) { setMsg('Salve a distribuidora antes de pré-visualizar as regras herdadas.'); return }
    const { data } = await api.get(`/distribuidoras/${sel.id}/regras?consumo_kwh=500&valor_conta=500`)
    setPreview(data)
  }
  const nomeGer = (id) => geradores.find((g) => g.id === id)?.nome || '—'

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-5">
        <Zap className="text-accent-500" size={22} />
        <h1 className="text-xl font-heading font-extrabold text-graphite flex-1">Distribuidoras</h1>
        <button onClick={() => abrir(null)} className="inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}><Plus size={16} /> Nova Distribuidora</button>
      </div>

      {!sel && (
        <div className={card + ' divide-y divide-neutral-100'}>
          {lista.length === 0 && <p className="p-6 text-sm text-neutral-500">Nenhuma distribuidora cadastrada.</p>}
          {lista.map((d) => (
            <button key={d.id} onClick={() => abrir(d)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50">
              <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center"><Zap size={16} className="text-accent-500" /></div>
              <div className="flex-1"><p className="font-semibold text-graphite text-sm">{d.nome} <span className="text-neutral-400 font-normal">· {d.estado}</span></p>
                <p className="text-xs text-neutral-500">Gerador principal: {nomeGer(d.gerador_principal_id)}</p></div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${(d.status_operacional || (d.ativo ? 'ativa' : 'inativa')) === 'ativa' ? 'bg-emerald-50 text-emerald-600' : (d.status_operacional === 'standby' ? 'bg-amber-50 text-amber-600' : 'bg-neutral-100 text-neutral-500')}`}>{d.status_operacional || (d.ativo ? 'ativa' : 'inativa')}</span>
              <ChevronRight size={16} className="text-neutral-400" />
            </button>
          ))}
        </div>
      )}

      {sel && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setSel(null)} className="text-sm text-neutral-500 hover:text-graphite inline-flex items-center gap-1"><X size={15} /> Fechar</button>
            <div className="flex items-center gap-2">
              {sel.id && <button onClick={excluir} className="text-sm text-red-600 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /> Excluir</button>}
              <button onClick={salvar} disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl text-white font-bold px-4 py-2 text-sm disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#ffcc00,#ff6c00)' }}><Save size={15} /> {busy ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
          {msg && <div className="p-3 rounded-xl bg-accent-50 border border-accent-200 text-accent-800 text-sm">{msg}</div>}

          <div className={card + ' p-4'}>
            <h3 className="font-heading font-bold text-graphite text-sm mb-3">Dados da Distribuidora</h3>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2"><label className={lbl}>Nome</label><input className={inp} value={sel.nome || ''} onChange={(e) => set('nome', e.target.value)} /></div>
              <div><label className={lbl}>Sigla</label><input className={inp} value={sel.sigla || ''} onChange={(e) => set('sigla', e.target.value)} /></div>
              <div><label className={lbl}>Estado (UF)</label>
                <select className={inp} value={sel.estado || ''} onChange={(e) => set('estado', e.target.value)}><option value="">—</option>{UFS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
              </div>
              <div><label className={lbl}>Status Operacional</label>
                <select className={inp} value={sel.status_operacional || 'ativa'} onChange={(e) => set('status_operacional', e.target.value)}><option value="ativa">Ativa</option><option value="standby">Stand by</option><option value="inativa">Inativa</option></select>
              </div>
            </div>
          </div>

          <div className={card + ' p-4'}>
            <h3 className="font-heading font-bold text-graphite text-sm mb-1 flex items-center gap-1.5"><Link2 size={15} className="text-accent-500" /> Geradores Parceiros vinculados</h3>
            <p className="text-[11px] text-neutral-400 mb-3">A distribuidora HERDA todas as regras do gerador principal (visível só p/ admin, máx. 2).</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={lbl}>Gerador Principal</label>
                <select className={inp} value={sel.gerador_principal_id || ''} onChange={(e) => set('gerador_principal_id', e.target.value ? Number(e.target.value) : null)}><option value="">—</option>{geradores.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}</select>
              </div>
              <div><label className={lbl}>Gerador Secundário (Backup)</label>
                <select className={inp} value={sel.gerador_secundario_id || ''} onChange={(e) => set('gerador_secundario_id', e.target.value ? Number(e.target.value) : null)}><option value="">—</option>{geradores.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}</select>
              </div>
            </div>
            <button onClick={verHeranca} className="mt-3 text-sm font-semibold text-accent-700 inline-flex items-center gap-1"><Eye size={14} /> Pré-visualizar regras herdadas</button>
            {preview?.regras && (
              <div className="mt-3 bg-neutral-50 rounded-xl p-3 text-xs text-neutral-700 grid sm:grid-cols-2 gap-y-1 gap-x-4">
                <span>Gerador: <b>{preview.regras.gerador?.nome || '—'}</b></span>
                <span>Tipo de fatura: <b>{preview.regras.tipo_fatura || '—'}</b></span>
                <span>Desconto aplicado (500 kWh): <b>{preview.regras.desconto_aplicado ?? 0}%</b></span>
                <span>Prazo injeção: <b>{preview.regras.prazo_medio_injecao_dias ?? '—'} dias</b></span>
                <span>Troca titularidade: <b>{preview.regras.troca_titularidade ? 'Sim' : 'Não'}</b></span>
                <span>Análise crédito: <b>{preview.regras.requer_analise_credito ? 'Sim' : 'Não'}</b></span>
                {preview.simulacao && <span className="sm:col-span-2 text-emerald-700">Economia estimada (conta R$500): <b>{'R$ ' + Number(preview.simulacao.economia_mensal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</b></span>}
              </div>
            )}
          </div>

          <div className={card + ' p-4'}>
            <h3 className="font-heading font-bold text-graphite text-sm mb-1 flex items-center gap-1.5"><AlertTriangle size={15} className="text-amber-500" /> Aviso Operacional (opcional)</h3>
            <p className="text-[11px] text-neutral-400 mb-2">Exibido ao licenciado durante o cadastro do cliente.</p>
            <textarea className={inp + ' min-h-[70px]'} value={sel.avisos_operacionais || ''} onChange={(e) => set('avisos_operacionais', e.target.value)} placeholder="Ex.: Prazo médio de conexão superior a 180 dias." />
          </div>
        </div>
      )}
    </div>
  )
}
